import { shaderMaterial } from '@react-three/drei';

const vertexShader = `

  uniform float pointSize;
  uniform float lidarCount;
  // 높이 자르기(VPF-5485). 켜져 있으면 데이터 좌표 z 가 maxHeight 이상인 점을 그리지 않습니다.
  uniform bool heightCutEnabled;
  uniform float maxHeight;
  uniform float coordMins[MAX_LIDARS];
  uniform float coordMaxs[MAX_LIDARS];
  uniform float colorR[MAX_LIDARS];
  uniform float colorG[MAX_LIDARS];
  uniform float colorB[MAX_LIDARS];
  // [intensity, lidarIndex, velocity, 0] 을 non-normalized uint8 로 받습니다.
  attribute vec4 pointInfo;
  varying vec3 vColor;
  #ifdef HAS_INTENSITY
  varying float vIntensity;
  #endif

  float dequantize(float value, float minValue, float maxValue) {
    if (maxValue == minValue) {
      return minValue;
    }

    return minValue + (value / 65535.0) * (maxValue - minValue);
  }

// 1/35
#define POINT_SIZE_FACTOR (28.5714)

  void main() {
    // 범위 밖 uniform 배열 접근은 GLSL ES 1.00 에서 undefined behavior 이므로 clamp 합니다.
    int id = int(clamp(pointInfo.y, 0.0, lidarCount - 0.1));
    vColor = vec3(colorR[id], colorG[id], colorB[id]);

    #ifdef HAS_INTENSITY
    vIntensity = pointInfo.x / 255.0;
    #endif

    #ifdef QUANTIZED_XYZ
    float coordMin = coordMins[id];
    float coordMax = coordMaxs[id];
    // position 이 Uint16 non-normalized 어트리뷰트라 0~65535 float 로 들어옵니다.
    vec3 decodedPosition = vec3(
      dequantize(position.x, coordMin, coordMax),
      dequantize(position.y, coordMin, coordMax),
      dequantize(position.z, coordMin, coordMax)
    );
    #else
    vec3 decodedPosition = position;
    #endif

    // 축 반전 전의 z 가 지면 기준 높이입니다.
    if (heightCutEnabled && decodedPosition.z >= maxHeight) {
      // 클립 공간 밖(z/w = 2)으로 보내 래스터화 전에 버립니다.
      gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      return;
    }

    // 모델공간
    #if defined(FLIP_X)
    decodedPosition.x = -decodedPosition.x;
    #endif
    #if defined(FLIP_Y)
    decodedPosition.y = -decodedPosition.y;
    #endif
    #if defined(FLIP_Z)
    decodedPosition.z = -decodedPosition.z;
    #endif

    // 괄호로 mat4×vec4 를 두 번 하게 묶는다. 괄호가 없으면 mat4×mat4 를 먼저 계산해
    // 버텍스마다 행렬 곱이 한 번 더 든다.
    vec4 clipPos = projectionMatrix * (modelViewMatrix * vec4(decodedPosition, 1.0));
    #if defined(FLIP_CLIP_X)
    clipPos.x = -clipPos.x;
    #endif
    #if defined(FLIP_CLIP_Y)
    clipPos.y = -clipPos.y;
    #endif
    #if defined(FLIP_CLIP_Z)
    clipPos.z = -clipPos.z;
    #endif

    gl_Position = clipPos;

    float effectivePointSize = pointSize * POINT_SIZE_FACTOR;
    gl_PointSize = max(effectivePointSize, 1.0);
  }
`;

/**
 * intensity 가 가장 낮은 포인트의 알파입니다.
 *
 * 0 으로 깔면 저반사 표면(검은 차량, 젖은 아스팔트)의 정상 반환값이 통째로
 * 사라지므로 바닥을 두고 그 위에서 선형 대응시킵니다.
 */
const MIN_INTENSITY_ALPHA = 0.35;

/**
 * 숫자를 GLSL float 리터럴로 만듭니다.
 *
 * GLSL ES 1.00 은 int 와 float 을 암묵 변환하지 않아 `mix(0, 1.0, x)` 는 컴파일
 * 에러입니다. 소수점이 없으면 `.0` 을 붙여 float 임을 보장하되, 자릿수를 고정하지
 * 않아 값이 반올림되지 않게 합니다.
 */
const toGlslFloat = (value: number) =>
  Number.isInteger(value) ? `${value}.0` : `${value}`;

const fragmentShader = `
  uniform float opacity;
  varying vec3 vColor;
  #ifdef HAS_INTENSITY
  varying float vIntensity;
  #endif

  void main() {
    #ifdef HAS_INTENSITY
      // intensity 0 -> ${MIN_INTENSITY_ALPHA}, 255 -> 1.0 선형.
      float alpha =
        opacity * mix(${toGlslFloat(MIN_INTENSITY_ALPHA)}, 1.0, vIntensity);
      gl_FragColor = vec4(vColor, alpha);
    #else
      gl_FragColor = vec4(vColor, opacity);
    #endif
  }
`;

const emptyFloatArray = () => new Float32Array();

/**
 * define 으로 포인트 레이아웃과 축별 반전을 제어합니다.
 * 레이아웃: QUANTIZED_XYZ, HAS_INTENSITY
 * 모델 공간: FLIP_X, FLIP_Y, FLIP_Z
 * 클립 공간: FLIP_CLIP_X, FLIP_CLIP_Y, FLIP_CLIP_Z
 * 사용 예: <customPointsMaterial2 defines={{ FLIP_Z: 1, QUANTIZED_XYZ: 1 }} ... />
 */
export const CustomPointsMaterial2 = shaderMaterial(
  {
    colorB: emptyFloatArray(),
    colorG: emptyFloatArray(),
    colorR: emptyFloatArray(),
    coordMaxs: emptyFloatArray(),
    coordMins: emptyFloatArray(),
    heightCutEnabled: false,
    lidarCount: 1,
    maxHeight: 0,
    opacity: 1,
    pointSize: 0.05,
  },
  vertexShader,
  fragmentShader,
);
